/**
 * Vigilancia de variación de tasas para el home de operador.
 *
 * El panel ya refresca USD/CLP, USDT→VES y VES→USDT cada 2.5 minutos. Este
 * watcher compara cada lectura contra la lectura anterior del mismo riel y
 * avisa cuando el salto supera el umbral, para que el operador no tenga que
 * mirar los números uno por uno.
 *
 * Es detección de front: solo corre mientras el operador tiene el home
 * abierto. Si en algún momento se quiere un push que llegue con la pantalla
 * cerrada, la comparación tiene que vivir en backend.
 */

/** Variación mínima (en %) que dispara el aviso. */
export const UMBRAL_VARIACION_PCT = 0.4;

export type RielTasa = 'USD_CLP' | 'USDT_VES' | 'VES_USDT';

export const ETIQUETA_RIEL: Record<RielTasa, string> = {
  USD_CLP: 'USD / CLP',
  USDT_VES: 'USDT → VES',
  VES_USDT: 'VES → USDT',
};

export interface AlertaVariacion {
  riel: RielTasa;
  etiqueta: string;
  anterior: number;
  actual: number;
  /** Variación porcentual con signo: negativa si la tasa bajó. */
  variacionPct: number;
  subio: boolean;
  detectadaEn: Date;
}

export class RateDriftWatcher {
  private ultimaLectura = new Map<RielTasa, number>();

  constructor(private readonly umbralPct: number = UMBRAL_VARIACION_PCT) {}

  /**
   * Registra una lectura y devuelve la alerta si el salto contra la lectura
   * previa supera el umbral. La primera lectura de cada riel solo fija la
   * base: nunca alerta. La base se mueve en cada lectura válida, así que dos
   * saltos consecutivos se miden uno contra otro, no contra el valor inicial.
   */
  registrar(
    riel: RielTasa,
    valor: number | null | undefined,
  ): AlertaVariacion | null {
    if (valor == null || !Number.isFinite(valor) || valor <= 0) return null;

    const anterior = this.ultimaLectura.get(riel);
    this.ultimaLectura.set(riel, valor);

    if (anterior == null || anterior <= 0) return null;

    const variacionPct = ((valor - anterior) / anterior) * 100;

    // El umbral es inclusivo: una variación de exactamente 0.4% avisa. El
    // epsilon está para que la coma flotante no la deje afuera (300 → 301.2
    // da 0.39999999999999147).
    if (Math.abs(variacionPct) + 1e-9 < this.umbralPct) return null;

    return {
      riel,
      etiqueta: ETIQUETA_RIEL[riel],
      anterior,
      actual: valor,
      variacionPct,
      subio: variacionPct > 0,
      detectadaEn: new Date(),
    };
  }
}
